type FormResults = Record<string, unknown>

type ComparisonOperator =
  '>' | '<' | '>=' | '<=' | '=' | '==' | '===' | '!=' | '!==' | 'includes' | 'excludes'

interface SingleCondition {
  formStepKey: string
  comparisonOperator: ComparisonOperator
  expectedAnswer: string | number | boolean
}

interface MultiCondition {
  operator: '&&' | '||'
  conditions: VisibleIfCondition[]
}

type VisibleIfCondition = string | SingleCondition | MultiCondition

function getValueAtPath(source: FormResults, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (current, key) =>
        current && typeof current === 'object'
          ? (current as Record<string, unknown>)[key]
          : undefined,
      source,
    )
}

function compare(
  actual: unknown,
  operator: ComparisonOperator,
  expected: string | number | boolean,
): boolean {
  switch (operator) {
    case '=':
    case '==':
      return actual == expected
    case '===':
      return actual === expected
    case '!=':
      return actual != expected
    case '!==':
      return actual !== expected
    case '>':
      return Number(actual) > Number(expected)
    case '<':
      return Number(actual) < Number(expected)
    case '>=':
      return Number(actual) >= Number(expected)
    case '<=':
      return Number(actual) <= Number(expected)
    case 'includes':
      return Array.isArray(actual) && actual.includes(expected)
    case 'excludes':
      return Array.isArray(actual) && !actual.includes(expected)
    default:
      return false
  }
}

// Parse "key op value" shorthand strings like 'interstitial = deepDive'.
function parseShorthand(condition: string): SingleCondition | null {
  const match = condition.trim().match(/^(\S+)\s*(===|!==|==|!=|>=|<=|=|>|<)\s*(.+)$/)
  if (!match) return null
  const [, formStepKey, operator, rawValue] = match
  if (!formStepKey || !operator || rawValue === undefined) return null
  const trimmed = rawValue.trim().replace(/^['"]|['"]$/g, '')
  let expectedAnswer: string | number | boolean = trimmed
  if (trimmed === 'true') expectedAnswer = true
  else if (trimmed === 'false') expectedAnswer = false
  else if (trimmed !== '' && !isNaN(Number(trimmed))) expectedAnswer = Number(trimmed)
  return {
    formStepKey,
    comparisonOperator: operator as ComparisonOperator,
    expectedAnswer,
  }
}

export function isStepVisible(
  visibleIf: VisibleIfCondition | undefined,
  formResults: FormResults,
): boolean {
  if (!visibleIf) return true

  if (typeof visibleIf === 'string') {
    const parsed = parseShorthand(visibleIf)
    return parsed ? isStepVisible(parsed, formResults) : true
  }

  if ('operator' in visibleIf) {
    const results = visibleIf.conditions.map((condition) => isStepVisible(condition, formResults))
    return visibleIf.operator === '&&' ? results.every(Boolean) : results.some(Boolean)
  }

  return compare(
    getValueAtPath(formResults, visibleIf.formStepKey),
    visibleIf.comparisonOperator,
    visibleIf.expectedAnswer,
  )
}
